import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppCardHeader from 'src/components/card/AppCardHeader'
import AppForm from '../../components/form/form-asset/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import FormFileThumbnail from 'src/components/form/general-input-form/FormFileThumbnail'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'
import AppModalFilePreview from 'src/components/modal/AppModalFilePreview'

// helpers
import Axios from '../../helpers/axios'
import useToastMessage from 'src/helpers/toast-message'

const AddAsset = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();

  const isLoading = useSelector((state) => state.isLoading)

  const [showDeleteItem, setShowDeleteItem] = useState({ show: false, item: {} })
  const [showConfirmAddAttachments, setShowConfirmAddAttachments] = useState({ show: false, item: {} })
  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })

  const [openFilePreview, setOpenFilePreview] = useState(false)

  const formik = useFormik({
    initialValues: {
      asset_name: '',
      asset_description: '',
      asset_register_date: '',
      asset_value: '',
      is_cash: false,
      attachments: []
    },
    validationSchema: Yup.object().shape({
      asset_name: Yup.string()
        .required("Please enter asset name."),
      asset_description: Yup.string()
        .required("Please enter asset description."),
      asset_register_date: Yup.string()
        .required("Please enter asset register date."),
      asset_value: Yup.string()
        .required("Please enter asset value."),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  const handleOnDateChange = (date, name) => {
    formik.setValues((values) => ({
      ...values,
      [name]: date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : ""
    }), false)
  }

  const handleInputValue = (values) => {
    const formData = new FormData()
    formData.append('name', values?.asset_name)
    formData.append('description', values?.asset_description)
    formData.append('register_date', values?.asset_register_date)
    formData.append('value', parseFloat(values?.asset_value.replace(/,/g, "")))
    formData.append('is_cash', values.is_cash)
    if (values?.attachments?.length > 0) {
      for (let i = 0; i < values.attachments.length; i++) {
        formData.append('file', values.attachments[i])
      }
    }

    return formData
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/asset/create`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('New asset has been successfully created')
          navigate('/asset/list')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleCardHeaderButton = () => {
    setShowConfirmAddAttachments({ show: true, item: {} })
  }

  const handleFileInput = (values, type) => {
    const files = values

    if (files) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        [type]: [...prevValues[type], ...files]
      }), false)
    }

    setShowConfirmAddAttachments({ show: false, item: {} })
  }

  const handleRemoveFile = (index = 0, name) => {
    formik.setValues((values) => ({
      ...values,
      [name]: values[name]?.filter((_, i) => i !== index)
    }), false)

    setShowDeleteItem({ show: false, type: '', item: {}, index: 0 })
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  return (
    <>
      {openFilePreview && (
        <AppModalFilePreview
          setOpenFilePreview={setOpenFilePreview}
          file={openFilePreview}
        />
      )}
      {
        showConfirmAddAttachments.show && (
          <AppModalConfirmation 
            confirmationType='add-attachments'
            itemName='attachments'
            itemDetails={showConfirmAddAttachments.item}
            showModal={showConfirmAddAttachments}
            setShowModal={setShowConfirmAddAttachments}
            errors={formik.errors?.attachments}
            handleChange={formik.handleChange}
            handleFileInput={handleFileInput}
            headerTitle={`Add Asset Attachments`}
          />
        )
      }
      {
        showDeleteItem.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={`${showDeleteItem?.item?.file_original_name ? showDeleteItem?.item?.file_original_name : showDeleteItem?.item?.name}`}
            itemDetails={showDeleteItem.item}
            itemType={showDeleteItem.type}
            itemIndex={showDeleteItem?.index}
            showModal={showDeleteItem}
            setShowModal={setShowDeleteItem}
            handleConfirmation={handleRemoveFile}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want create a new asset?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Add Asset</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            onDateChange={handleOnDateChange}
          />
        }
        lowerBodyContent={
          <AppCard 
            className='mb-4'
            headerTitle={
              <AppCardHeader
                headerTitle={<h4>Attachments</h4>}
                headerButton="Upload new"
                handleCardHeaderButton={handleCardHeaderButton}
              />
            }
            bodyContent={
              <>
                {
                  formik?.values?.attachments?.length > 0 ? (
                    <FormFileThumbnail 
                      name='attachments'
                      colSize='3'
                      files={formik?.values?.attachments}
                      className='justify-content-start align-items-center'
                      handleConfirmDelete={setShowDeleteItem}
                      handleViewFile={setOpenFilePreview}
                    />
                  )
                  : (
                    <>Not Found</>
                  )
                }
              </>
            }
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate('/asset/list')}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default AddAsset